// Which server a build talks to, chosen by name from a list rather than typed as
// an address.
//
// WHY A NAME AND NOT A URL.
//
// The address is still inlined at build time and that is not what changed — see
// boot.ts for why it must not come from a screen. What changed is what a person
// types to produce a build. `VITE_NOVA_BASE_URL=https://…run.app` is a value
// nothing can check: every misspelling of it is a plausible URL, the shell has
// no way to tell a rehearsal host from a live one, and there is nothing to put
// on a screen except the URL itself, which is the thing nobody reads. A name
// can be got right from memory, can be checked against this list, and is short
// enough to say out loud to somebody holding the tablet.
//
// It cost twenty minutes on 2026-08-26 in the cheapest form available: the
// variable was simply forgotten, the fallback was the empty string, and the
// tablet reported that it could not reach the server. §104 in TASKS.md. The
// expensive form is the one this file is for, and it has not happened yet: a
// build pointed at a real server that is the wrong real server, which fails by
// working.
//
// THIS FILE IS THE ONE BOTH THE BUILD AND THE DEVICE READ.
//
// vite.config.ts imports readEnvironment and refuses a build that does not
// resolve, and thisBuild.ts calls the same function over the same variables at
// run time. One list, one set of rules, two readers. A build tool with its own
// copy of the table is a build tool that eventually permits something the device
// refuses, and the symptom is an artefact that installs and will not start.
//
// So nothing here may touch `import.meta`: this module is imported by
// vite.config.ts, which runs in Node.

/** An environment a build may be made for, and everything the shell says about it. */
export interface KnownEnvironment {
  /**
   * Where the server is, or null for an environment that has a name and does
   * not exist yet.
   *
   * Null is refused rather than treated as permission, the same way
   * xp/xp.config.json's nulls are refused by the deploy guard. It is not the
   * same thing as an unknown name, and the two get different sentences: one
   * means "there is no such environment" and the other means "there is, and it
   * has not been built".
   */
  readonly baseUrl: string | null;
  /**
   * Where the public registration page lives for this build, or null where this
   * build must not draw a QR.
   *
   * Separate from `baseUrl`, which is the API. A QR of the API host would send
   * a visitor to Fastify. A QR of a guessed registration host is the
   * wrong-server problem as a picture somebody prints. Production is the
   * Hosting site behind the Mailgun sending domain: holding page at `/`,
   * join app at the stand and magic-link paths. Filled when Phase 6 served
   * that split.
   */
  readonly webOrigin: string | null;
  /**
   * Where the kiosk player lives for this build, or null where this build
   * must not form a screen address.
   *
   * Separate from `baseUrl`, which is the API, and from `webOrigin`, which is
   * registration. A QR or a pasted address of a guessed player host is a stand
   * that points the hall at the wrong server. Production is the Hosting
   * site behind kiosk.novaexhibits.com, filled when Phase 5 served a
   * production build there.
   */
  readonly kioskOrigin: string | null;
  /**
   * Where the console talks, or null while the admin service is not deployed.
   *
   * Separate from `baseUrl`, which is the tablet-facing API. After Phase 4 the
   * console points wholly at the admin service. Null means the console still
   * talks to `baseUrl` — the combined process — rather than to a URL that 404s.
   */
  readonly adminUrl: string | null;
  /**
   * Whether a real attendee's details may be captured against this server.
   *
   * The same field name as `xp/xp.config.json` uses for the same fact, because
   * this is that fact read from the tablet's end. It decides one thing here: an
   * environment that may not hold real attendee data is watermarked on every
   * screen, and the live one is not. See serverWatermark.
   */
  readonly allowsRealAttendeeData: boolean;
  /**
   * Whether the address may be given at build time rather than taken from here.
   *
   * True only for `local`, which by definition has no fixed address: the
   * harness binds a port that its own error message tells you to change when it
   * is taken. Every other environment is a deployment with one address, and an
   * address passed alongside its name is refused rather than ignored — see
   * readEnvironment.
   */
  readonly addressMayBeGiven: boolean;
  /** What this server is, for a crew member reading a screen. */
  readonly what: string;
}

/**
 * Every environment a build may name.
 *
 * ADDING ONE IS AN EDIT HERE AND NOWHERE ELSE, AND THAT IS THE POINT.
 *
 * An address that is not in this list cannot be built against. The alternative
 * — accepting any URL and treating the list as a set of shortcuts — keeps
 * exactly the habit that produced §104 and gives the screen nothing to name,
 * because a mark that reads `https://192.168.1.14:4310` is a mark nobody checks
 * against anything. So a new server is a line here, in a diff somebody reviews,
 * with a name the tablet can say.
 */
export const ENVIRONMENTS = {
  local: {
    // The harness, which binds 127.0.0.1 and nothing else. A device therefore
    // cannot reach it at all; this is for the browser development build and for
    // the simulator-shaped work that never happens on glass.
    baseUrl: "http://127.0.0.1:4310",
    // xp/web's Vite port. A QR of the API harness (4310) would send a visitor
    // to Fastify, not to the join page.
    webOrigin: "http://localhost:4330",
    // xp/kiosk's Vite port, pinned in kiosk/vite.config.ts the way the
    // console pins 5173: a drifting port is a launch address that 404s.
    kioskOrigin: "http://localhost:4340",
    adminUrl: "http://127.0.0.1:4310",
    allowsRealAttendeeData: false,
    addressMayBeGiven: true,
    what: "a harness on the machine this build was made on",
  },
  staging: {
    baseUrl: "https://xp1-api-staging-739319167448.europe-west2.run.app",
    webOrigin: "https://reg-staging.novaexhibits.com",
    kioskOrigin: "https://kiosk-staging.novaexhibits.com",
    // FILLED 2026-09-12 when xp1-admin-staging came up. The console talks
    // here; tablets, kiosk playback, reg and the game stay on baseUrl.
    adminUrl: "https://xp1-admin-staging-739319167448.europe-west2.run.app",
    allowsRealAttendeeData: false,
    addressMayBeGiven: false,
    what: "the rehearsal service in London, which holds no real attendee data",
  },
  production: {
    // FILLED 2026-09-12, AND THE LINE IS THE LOCK NOW.
    //
    // This row was null from the day the table was written until the production
    // Cloud Run service existed and had been checked live (PRODUCTION.md step
    // 18, scripts/check-cloud.mjs, no failures). The address is the service's
    // own `run.app` hostname rather than `api.novaexhibits.com`, by decision
    // (xp/PRODUCTION-GO-LIVE.md, Gate A): a custom hostname for the API needs a
    // load balancer this project does not yet run, and a tablet built against
    // this string cannot be repointed without a new build, so the string chosen
    // is the one that exists. If `api.` is ever put in front of the service,
    // that is a reviewed edit here and a rebuild of every tablet.
    //
    // It is the same string xp/xp.config.json holds as
    // environments.production.service, and app/check.mjs asserts the two are
    // equal rather than either being right on its own.
    //
    // What still makes production the hardest thing to select by accident:
    // nothing in package.json builds it — `VITE_XP_ENV=production` is typed in
    // full — and the watermark rule below marks every other environment and
    // not this one, so a rehearsal build at a real stand says so on every
    // screen.
    baseUrl: "https://xp1-api-739319167448.europe-west2.run.app",
    // FILLED 2026-09-13 when xp1-reg started serving the join app at the
    // stand paths without replacing the holding page at /. A QR of this
    // origin plus /j/<activation> is the page. The hostname is the Mailgun
    // sending domain; do not invent a different one.
    webOrigin: "https://reg.novaexhibits.com",
    // FILLED 2026-09-13 when xp1-kiosk-production started serving a
    // production build. The custom domain is attached in the Firebase
    // console, not invented here.
    kioskOrigin: "https://kiosk.novaexhibits.com",
    // FILLED 2026-09-12 when xp1-admin revision 00001 came up. The console talks
    // here; tablets, kiosk playback, reg and the game stay on baseUrl.
    adminUrl: "https://xp1-admin-739319167448.europe-west2.run.app",
    allowsRealAttendeeData: true,
    addressMayBeGiven: false,
    what: "the live service",
  },
} as const satisfies Record<string, KnownEnvironment>;

export type EnvironmentName = keyof typeof ENVIRONMENTS;

/** In the order a person meets them, which is the order they are printed in. */
export const ENVIRONMENT_NAMES = Object.keys(ENVIRONMENTS) as EnvironmentName[];

/** The variable that names an environment. One string, so the check can find it. */
export const ENV_VARIABLE = "VITE_XP_ENV";

/**
 * The variable that used to carry the address, still read.
 *
 * Kept rather than renamed, and kept for one reason: it is what fingers already
 * type. A build made with the old command and none of the new one now refuses
 * and says what to do, where a renamed variable would have been silently
 * ignored by Vite and produced a build pointed somewhere the person did not
 * intend. It survives as the local address override and as a tripwire for the
 * habit it used to serve.
 */
export const ADDRESS_VARIABLE = "VITE_NOVA_BASE_URL";

export type EnvironmentReading =
  | {
      ok: true;
      name: EnvironmentName;
      baseUrl: string;
      allowsRealAttendeeData: boolean;
      /** True where the address came from the command line rather than from the table. */
      addressWasGiven: boolean;
    }
  /**
   * One sentence, written to be read in two places: thrown by the build, and
   * printed on the tablet's own fault screen. Both readers are the same person
   * on different days, and a message written for a terminal is a message a crew
   * member cannot act on.
   */
  | { ok: false; problem: string };

/**
 * Resolve the two build-time variables into a server, or into a refusal.
 *
 * Pure, and takes both values as arguments rather than reading them, so that
 * the build tool, the device and the check all drive the same code over inputs
 * they choose.
 */
export function readEnvironment(name: string | undefined, address: string | undefined): EnvironmentReading {
  const chosen = (name ?? "").trim();
  const given = address?.trim();

  if (chosen === "") {
    // No default, and this is the answer to the item's own question about what
    // a plain `npm run build` should do. A default of `staging` would make the
    // §104 artefact impossible and make its expensive sibling likelier: every
    // forgotten variable becomes a build that works, aimed at a server nobody
    // chose, and the whole finding was that a build nobody chose is the
    // dangerous one. A default of `local` produces an artefact that cannot
    // reach anything from a device, which is §104 with a nicer message.
    return {
      ok: false,
      problem: `This build does not say which server it belongs to. ${ENV_VARIABLE} was not set, so nothing decided which environment it is for. The names are ${list(ENVIRONMENT_NAMES)}.`,
    };
  }

  const known = (ENVIRONMENTS as Record<string, KnownEnvironment | undefined>)[chosen];

  if (known === undefined) {
    // The likeliest wrong value is not a misspelling, it is the old habit: an
    // address pasted where a name goes. Said separately, because "staging is
    // not one of local, staging, production" is a confusing sentence to read
    // when what you typed was a URL.
    return {
      ok: false,
      problem: /^[a-z]+:\/\//i.test(chosen)
        ? `${ENV_VARIABLE} was given an address rather than the name of an environment. The names are ${list(ENVIRONMENT_NAMES)}. An address that ought to be buildable belongs in ENVIRONMENTS in src/server/environments.ts, where it has a name this tablet can put on its own screen.`
        : `${ENV_VARIABLE} names ${JSON.stringify(chosen)}, which is not an environment this application knows. The names are ${list(ENVIRONMENT_NAMES)}.`,
    };
  }

  if (known.baseUrl === null) {
    return {
      ok: false,
      problem: `${ENV_VARIABLE} names ${chosen}, and there is no ${chosen} deployment of XP1 yet. Its address is null in src/server/environments.ts and in xp/xp.config.json, and a null there is refused rather than guessed at.`,
    };
  }

  if (given === undefined) {
    return {
      ok: true,
      name: chosen as EnvironmentName,
      baseUrl: known.baseUrl,
      allowsRealAttendeeData: known.allowsRealAttendeeData,
      addressWasGiven: false,
    };
  }

  if (!known.addressMayBeGiven) {
    // Refused rather than ignored. A variable that is present and disregarded
    // is the worst of the three outcomes: the person who set it believes it
    // took effect, and the build they get is aimed somewhere else entirely.
    return {
      ok: false,
      problem: `${ADDRESS_VARIABLE} was set as well as ${ENV_VARIABLE}=${chosen}, and ${chosen} has one address, which is in src/server/environments.ts. Remove ${ADDRESS_VARIABLE}: an address given here would either be the one already on file or a different one nothing on the tablet would ever say out loud.`,
    };
  }

  if (given === "") {
    // The §104 value itself, arriving through the one door still open to it.
    return {
      ok: false,
      problem: `${ADDRESS_VARIABLE} was set to nothing at all. An empty address makes every request relative, which on a tablet means aimed at the web view's own origin, where nothing is listening — the tablet then reports that it cannot reach the server, which is true and reads as a network fault.`,
    };
  }

  const local = localAddressProblem(given);
  if (local !== null) return { ok: false, problem: local };

  return {
    ok: true,
    name: chosen as EnvironmentName,
    baseUrl: given,
    allowsRealAttendeeData: known.allowsRealAttendeeData,
    addressWasGiven: true,
  };
}

/**
 * Why a given local address is not one, or null if it is fine.
 *
 * THE BOUND IS WHAT KEEPS THE WORD "LOCAL" HONEST.
 *
 * Without it, `local` plus an address is a way to build a tablet aimed at any
 * host on the internet while every screen on it says local — which is precisely
 * the silent wrong-server case this whole item exists for, reintroduced by the
 * escape hatch meant to make development possible. So the address has to be a
 * loopback one. What moves is the port, because that is what the harness's own
 * "port 4310 is taken" message tells you to move; the host does not, because
 * the harness binds 127.0.0.1 and refuses to be anywhere else.
 *
 * A machine on the same desk reached over the network is therefore not
 * expressible here, on purpose: it wants a name in ENVIRONMENTS, which is one
 * line and gives the tablet something to say.
 */
function localAddressProblem(address: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(address);
  } catch {
    return `${ADDRESS_VARIABLE} is ${JSON.stringify(address)}, which is not an address. It has to be an absolute one — a relative address is aimed at the tablet itself.`;
  }

  if (parsed.protocol !== "http:") {
    return `${ADDRESS_VARIABLE} is ${JSON.stringify(address)}. A local harness speaks http, and https to a loopback address is a certificate error rather than a connection.`;
  }

  if (parsed.hostname !== "127.0.0.1" && parsed.hostname !== "localhost") {
    return `${ADDRESS_VARIABLE} is ${JSON.stringify(address)}, which is not on this machine. ${ENV_VARIABLE}=local may only be pointed at 127.0.0.1 or localhost, so that nothing labelled local is talking to a server on the internet. Give that host a name in ENVIRONMENTS in src/server/environments.ts instead.`;
  }

  // station's httpTransport builds a request as `${baseUrl}${endpoint}` and
  // every endpoint begins with a slash, so a trailing one produces `//v1/…`.
  // Refused rather than trimmed: it is a typo, and trimming it teaches nobody.
  if (parsed.pathname !== "/" || address.endsWith("/")) {
    return `${ADDRESS_VARIABLE} is ${JSON.stringify(address)}, which carries a path. The address is a host and a port and nothing after it, because every request appends its own path to it.`;
  }

  return null;
}

/**
 * What the watermark says, or null where there should not be one.
 *
 * THE RULE IS ABOUT ATTENDEE DATA RATHER THAN ABOUT WHICH BUILD IS SPECIAL.
 *
 * A mark across an attendee-facing capture screen at a real show would be
 * unacceptable, and a real show is the live environment, which is the one that
 * returns null here. Every other case is a tablet whose captures do not count,
 * and a tablet whose captures do not count standing at a stand taking people's
 * details is the worst outcome in this whole item — worse than a build that
 * cannot start, because it looks like it is working and the day's registrations
 * are gone. So that one is interrupted on every screen, permanently, in the
 * same place and for the same reason as the browser development mark in
 * dev/flag.ts.
 *
 * A build that resolved to nothing is marked too. It cannot hold real attendee
 * data either, so the rule has no exception to remember.
 */
export function serverWatermark(reading: EnvironmentReading): string | null {
  if (!reading.ok) {
    return "THIS BUILD NAMES NO SERVER — IT CANNOT SEND ANYTHING ANYWHERE";
  }
  if (reading.allowsRealAttendeeData) return null;
  return `${reading.name.toUpperCase()} SERVER — NOT THE LIVE ONE — ANYTHING CAPTURED HERE IS A REHEARSAL — ${host(reading.baseUrl)}`;
}

/**
 * The same fact in a sentence, for the screens a crew member reads.
 *
 * Said on the live environment as well, unlike the watermark. A panel that
 * names the server only when it is the wrong one is a panel that cannot be used
 * to confirm it is the right one, and confirming is what somebody setting a
 * tablet up is actually doing.
 */
export function serverSentence(reading: EnvironmentReading): string {
  if (!reading.ok) {
    return "This build does not name a server, so this tablet cannot reach one. It has to be built again.";
  }
  const known = ENVIRONMENTS[reading.name];
  const where = reading.addressWasGiven ? `${host(reading.baseUrl)}, given at build time` : host(reading.baseUrl);
  return `This tablet sends to the ${reading.name} server: ${known.what}, at ${where}.`;
}

/** Host and port, because the scheme and the path are the same on every one of them. */
function host(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

const list = (names: readonly string[]): string =>
  names.length < 2
    ? (names[0] ?? "none")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
